// Agent ticket creation — route /tickets/new.
//
// ~44 tickets a day are typed here while a customer is on the phone, which makes
// this screen's speed part of call handling time (SPEC.md §6). Everything below is
// arranged around that:
//
//   * Keyboard-first. The account-number field takes focus on arrival, tab order
//     follows the order of a call (who is calling → what happened → how it is
//     filed), Ctrl/⌘+Enter submits from anywhere, and Ctrl/⌘+Shift+Enter creates
//     and solves in one action. Nothing on the screen traps focus.
//   * One request per ticket. POST /api/tickets takes the ticket, tags, CCs,
//     followers, custom fields and the opening message together and returns the
//     full detail shape, so a successful create routes straight to the ticket with
//     no second fetch (docs/API.md §3.3). A call that produced several issues files
//     them all from here: the (+) under the main ticket adds child tickets, created
//     after the parent with its id, so the agent never leaves the screen mid-call.
//   * A draft that survives. Everything typed is mirrored to localStorage, because
//     losing a half-entered ticket to a stray navigation means asking a customer to
//     repeat themselves.
//
// Config (forms, users, queues, tags, macros, statuses) loads per-request and
// degrades: a lookup that fails leaves its picker empty and the ticket still
// creatable, rather than taking the screen down over a dropdown.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { api, ApiError } from '../../api/client.js';
import { useSession, useCanWrite } from '../../session/SessionProvider.jsx';
import CallerPanel, { EMPTY_CALLER } from '../../components/create/CallerPanel.jsx';
import Composer from '../../components/create/Composer.jsx';
import {
  visibleFieldsWithRequirement, valuesForVisibleFields,
} from '../../components/create/form-conditions.js';
import CustomFields, {
  encodeCustomFields,
  invalidNumberFields,
  missingRequiredFields,
} from '../../components/create/CustomFields.jsx';
import CustomerContext from '../../components/create/CustomerContext.jsx';
import TagInput from '../../components/create/TagInput.jsx';
import ChipSelect from '../../components/create/ChipSelect.jsx';
import { fillMacroTemplate } from '../../components/create/macro-placeholders.js';
import { loadDraft, saveDraft, clearDraft, draftHasContent } from '../../components/create/draft.js';
import { prefillFromParent, assigneeFor } from '../../components/create/child-prefill.js';
import { typeOptionsFor } from './ticket-type.js';
import TicketLinkPicker from '../../components/tickets/TicketLinkPicker.jsx';
import ChildTicketCard from '../../components/create/ChildTicketCard.jsx';
import {
  childBody, createLabel, newChildDraft, validateChild,
} from '../../components/create/child-tickets.js';

const EMPTY_FIELDS = {
  channel: 'phone',
  priority: 'normal',
  // '' = not categorised; the macro applier's set_type writes here.
  type: '',
  status: 'new',
  status_id: null,
  queue_id: '',
  assigned_user_id: '',
  form_id: '',
  device_serial: '',
  tags: [],
  follower_user_ids: [],
  cc_customer_ids: [],
  custom_fields: {},
  // Related tickets to link once the ticket exists (docs/API.md §3.7) — a link needs
  // two ticket numbers and this one has none yet. `{ id, public_ref, subject }` rows.
  links: [],
  // The ticket this one is a sub-issue of (`?parent=` → `parent_ticket_id`), or null.
  // Kept in fields rather than beside them so it survives in the draft.
  parent: null,
  // Other issues from the SAME call, each filed as a child of this ticket when it is
  // created — the (+) under the main ticket (child-tickets.js). Also in the draft.
  children: [],
};

const EMPTY_COMPOSER = { subject: '', body_text: '', is_internal_note: true };

const EMPTY_CONFIG = {
  loaded: false,
  failed: [],
  forms: [],
  assignees: [],
  users: [],
  queues: [],
  tags: [],
  macros: [],
  statuses: [],
};

// Loaded together, each independently: one 404 from an endpoint another agent has
// not landed yet must not blank the screen.
const CONFIG_REQUESTS = [
  ['forms', '/ticket-forms'],
  ['assignees', '/users?can_be_assigned=true'],
  ['users', '/users'],
  ['queues', '/queues'],
  ['tags', '/tags'],
  ['macros', '/macros'],
  ['statuses', '/ticket-statuses'],
];

const BASE_STATUSES = [
  ['new', 'New'],
  ['open', 'Open'],
  ['pending', 'Pending'],
  ['on_hold', 'On hold'],
  ['solved', 'Solved'],
  ['closed', 'Closed'],
];

const PRIORITIES = [
  ['low', 'Low'],
  ['normal', 'Normal'],
  ['high', 'High'],
  ['urgent', 'Urgent'],
];

export default function TicketNew() {
  const user = useSession();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `/tickets/new?parent=10228` — the (+) on a ticket's Related panel. Everything
  // editable is copied from that ticket once it loads (child-prefill.js).
  const parentRef = searchParams.get('parent');

  const [initialDraft] = useState(loadDraft);
  const [caller, setCaller] = useState(() => ({ ...EMPTY_CALLER, ...(initialDraft?.caller ?? {}) }));
  // The default owner is the agent creating the ticket (docs/API.md §3.3) — set here,
  // under any restored draft, so a draft that deliberately cleared it stays cleared.
  const [fields, setFields] = useState(() => ({
    ...EMPTY_FIELDS,
    assigned_user_id: assigneeFor(user),
    ...(initialDraft?.fields ?? {}),
  }));
  const [parentState, setParentState] = useState(() => ({ status: parentRef ? 'loading' : 'idle', error: null }));
  const [composer, setComposer] = useState(() => ({ ...EMPTY_COMPOSER, ...(initialDraft?.composer ?? {}) }));
  const [draftBanner, setDraftBanner] = useState(() => (initialDraft ? initialDraft.saved_at : null));

  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [people, setPeople] = useState({ orgId: null, rows: [], status: 'idle', error: null });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Set once the main ticket has been created and a child then failed. A retry must
  // file the remaining children under THIS ticket, never create a second parent.
  const [createdParent, setCreatedParent] = useState(null);
  const childSubjectRefs = useRef(new Map());

  const searchRef = useRef(null);
  const subjectRef = useRef(null);
  const errorRef = useRef(null);
  const submitRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    Promise.all(
      CONFIG_REQUESTS.map(([key, path]) =>
        api
          .get(path, { signal: controller.signal })
          .then((response) => [key, response.data ?? [], null])
          .catch((error) => [key, [], error]),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next = { ...EMPTY_CONFIG, loaded: true, failed: [] };
      for (const [key, data, error] of results) {
        next[key] = data;
        if (error) next.failed.push(key);
      }
      setConfig(next);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // The parent ticket, when this is a child. Loaded once and copied over whatever was
  // on the screen — a restored draft included, because the agent clicked (+) on a
  // specific ticket and that intent is newer than the draft.
  useEffect(() => {
    if (!parentRef) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setParentState({ status: 'loading', error: null });
    api
      .get(`/tickets/${encodeURIComponent(parentRef)}`, { signal: controller.signal })
      .then((parent) => {
        if (cancelled) return;
        const filled = prefillFromParent(parent, user);
        setCaller(filled.caller);
        setFields({
          ...filled.fields,
          parent: { id: parent.id, public_ref: parent.public_ref, subject: parent.subject },
        });
        setComposer(filled.composer);
        setDraftBanner(null);
        setParentState({ status: 'ready', error: null });
        subjectRef.current?.focus();
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setParentState({ status: 'error', error });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `user` is stable for the session; re-running on it would re-copy the parent over
    // the agent's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentRef]);

  // Defaults come from config, but never over the top of a restored draft.
  useEffect(() => {
    if (!config.loaded) return;
    setFields((prev) => {
      const next = { ...prev };
      if (!next.form_id) next.form_id = config.forms.find((form) => form.is_default)?.id ?? '';
      if (!next.queue_id) next.queue_id = config.queues.find((queue) => queue.is_default)?.id ?? '';
      return next;
    });
  }, [config.loaded, config.forms, config.queues]);

  // The organization's people serve two consumers — the requester list and the CC
  // picker — so they are fetched once here rather than twice below.
  const organizationId = caller.organization?.id ?? null;
  useEffect(() => {
    if (!organizationId) {
      setPeople({ orgId: null, rows: [], status: 'idle', error: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setPeople({ orgId: organizationId, rows: [], status: 'loading', error: null });

    api
      .get(`/organizations/${organizationId}/customers`, { signal: controller.signal })
      .then((response) => {
        if (!cancelled) setPeople({ orgId: organizationId, rows: response.data ?? [], status: 'ready', error: null });
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setPeople({ orgId: organizationId, rows: [], status: 'error', error });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [organizationId]);

  const defaultAssigneeId = assigneeFor(user);

  useEffect(() => {
    const draft = { caller, fields, composer };
    if (!draftHasContent(draft, { defaultAssigneeId })) {
      clearDraft();
      return undefined;
    }
    const timer = setTimeout(() => saveDraft(draft), 400);
    return () => clearTimeout(timer);
  }, [caller, fields, composer]);

  const selectedForm = useMemo(
    () => config.forms.find((form) => form.id === fields.form_id) ?? null,
    [config.forms, fields.form_id],
  );
  // CONDITIONAL FIELDS (migration 029, imported from the desk's own forms). A field
  // named as the child of a condition stays hidden until its parent takes the value
  // that asks for it, and becomes required if the condition says so. Everything
  // downstream -- validation, encoding, submit -- reads THIS list rather than the form's
  // full one, so a hidden field cannot be required and cannot be sent.
  const formFields = useMemo(
    () => visibleFieldsWithRequirement(
      selectedForm?.fields ?? [], selectedForm?.conditions ?? [], fields.custom_fields),
    [selectedForm, fields.custom_fields],
  );

  function setField(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  function validate() {
    const found = {};
    if (!caller.customer) found.customer = 'Choose who is calling before creating the ticket.';
    if (!composer.subject.trim()) found.subject = 'A subject is required.';
    if (!composer.is_internal_note && !user.can_send_customer_email) {
      found.message = 'Your role cannot send customer-facing email. Switch the message to an internal note.';
    }
    for (const field of missingRequiredFields(formFields, fields.custom_fields)) {
      found[`custom.${field.key}`] = `${field.label} is required on this form.`;
    }
    for (const field of invalidNumberFields(formFields, fields.custom_fields)) {
      found[`custom.${field.key}`] = `${field.label} must be a number.`;
    }
    // Each child is a whole ticket and is checked like one; its problems are keyed
    // `child.<key>.<field>` so the card can point at its own inputs and the summary
    // can say which child.
    fields.children.forEach((child, index) => {
      const childForm = config.forms.find((form) => form.id === child.fields.form_id) ?? null;
      for (const [key, message] of Object.entries(validateChild(child, childForm?.fields ?? [], user))) {
        found[`child.${child.key}.${key}`] = `Child ticket ${index + 1}: ${message}`;
      }
    });
    return found;
  }

  /** The problems belonging to one child card, with the `child.<key>.` prefix removed. */
  function childErrors(child) {
    const prefix = `child.${child.key}.`;
    const own = {};
    for (const [key, message] of Object.entries(errors)) {
      if (key.startsWith(prefix)) own[key.slice(prefix.length)] = message.replace(/^Child ticket \d+: /, '');
    }
    return own;
  }

  function addChild() {
    const child = newChildDraft(fields, composer);
    setFields((prev) => ({ ...prev, children: [...prev.children, child] }));
    // Focus the new card's subject once it has rendered — the agent is mid-call and
    // the next thing they type is what the second issue is.
    setTimeout(() => childSubjectRefs.current.get(child.key)?.focus(), 0);
  }

  function updateChild(next) {
    setFields((prev) => ({
      ...prev,
      children: prev.children.map((child) => (child.key === next.key ? next : child)),
    }));
  }

  function removeChild(key) {
    setFields((prev) => ({ ...prev, children: prev.children.filter((child) => child.key !== key) }));
  }

  function buildBody(status, statusId) {
    const body = {
      subject: composer.subject.trim(),
      channel: fields.channel,
      priority: fields.priority,
      status,
      customer_id: caller.customer.id,
      tags: fields.tags,
    };

    // '' is how the API clears a type; an untouched select sends it too, which is null.
    body.type = fields.type || '';
    if (statusId) body.status_id = statusId;
    // Organization and location are denormalized onto the ticket at creation. Sent
    // explicitly because the agent's choice is the organization the call was raised
    // for, which can differ from the requester's current one (docs/API.md §3.3).
    if (caller.organization) body.organization_id = caller.organization.id;
    if (caller.location) body.location_id = caller.location.id;
    if (fields.queue_id) body.queue_id = fields.queue_id;
    // ALWAYS sent. The server reads an absent key as "the creator" and an explicit null
    // as "nobody — let routing pick" (docs/API.md §3.3); the picker shows the agent
    // exactly one of those, so the request must say which.
    body.assigned_user_id = fields.assigned_user_id || null;
    if (fields.parent) body.parent_ticket_id = fields.parent.id;
    if (fields.form_id) body.form_id = fields.form_id;
    if (fields.device_serial.trim()) body.device_serial = fields.device_serial.trim();
    if (fields.follower_user_ids.length) body.follower_user_ids = fields.follower_user_ids;

    // A CC that is also the requester is 422 CC_IS_REQUESTER; the picker already
    // hides them, but a requester swapped after the CC was added would slip through.
    const ccs = fields.cc_customer_ids.filter((id) => id !== caller.customer.id);
    if (ccs.length) body.cc_customer_ids = ccs;

    // Only the answers to questions the form is actually asking. A value left behind by
    // a field that has since been hidden is an answer to a question nobody is being
    // asked -- Zendesk drops those too, and keeping one would file, say, a DoorDash
    // go-live date against a ticket whose integration was changed to something else.
    const custom = encodeCustomFields(formFields, valuesForVisibleFields(
      selectedForm?.fields ?? [], selectedForm?.conditions ?? [], fields.custom_fields));
    if (Object.keys(custom).length) body.custom_fields = custom;

    const bodyText = composer.body_text.trim();
    if (bodyText) {
      body.initial_message = {
        body_text: bodyText,
        is_internal_note: composer.is_internal_note,
      };
    }

    return body;
  }

  async function handleSubmit(override) {
    if (submitting || !canWrite) return;

    const status = override?.status ?? fields.status;
    // An overridden status (the create-and-solve shortcut) drops the named status:
    // a name that rolls up to a different base is 422 STATUS_BASE_MISMATCH.
    const statusId = override ? null : fields.status_id;

    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      setSubmitError(null);
      errorRef.current?.focus();
      return;
    }

    setErrors({});
    setSubmitError(null);
    setSubmitting(true);

    let ticket = createdParent;
    try {
      // The main ticket, unless an earlier attempt already created it and a child
      // failed — then the retry picks up where it stopped, under the same parent.
      if (!ticket) {
        ticket = await api.post('/tickets', buildBody(status, statusId));
        setCreatedParent(ticket);
        // Related tickets are linked AFTER the create, one call each: a link needs
        // both numbers, and the ticket had none until now. A failed link is not a
        // failed ticket — the agent lands on the detail, where the Related panel
        // shows what did land and offers the box again.
        for (const link of fields.links) {
          try {
            await api.post(`/tickets/${ticket.public_ref}/links`, { ticket: `#${link.public_ref}` });
          } catch {
            // Deliberately swallowed: see above.
          }
        }
      }

      // Then each child, in the order the agent added them, with the parent's id.
      // A child that lands is removed from the draft as it goes, so a failure part
      // way through leaves exactly the unfiled ones on screen for the retry.
      for (const child of fields.children) {
        const childForm = config.forms.find((form) => form.id === child.fields.form_id) ?? null;
        await api.post('/tickets', childBody(child, { caller, fields }, ticket.id, childForm?.fields ?? []));
        setFields((prev) => ({ ...prev, children: prev.children.filter((other) => other.key !== child.key) }));
      }

      clearDraft();
      navigate(`/tickets/${ticket.public_ref}`);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
        const fromServer = {};
        for (const entry of error.details?.fields ?? []) {
          fromServer[entry.field] = entry.message;
        }
        setErrors(fromServer);
      }
      setSubmitError(error);
      setSubmitting(false);
      errorRef.current?.focus();
    }
  }

  // The shortcut handler is registered once and reads the latest submit through a
  // ref: re-binding a window listener on every keystroke would be its own bug.
  useEffect(() => {
    submitRef.current = handleSubmit;
  });

  useEffect(() => {
    function onKeyDown(event) {
      if (!(event.ctrlKey || event.metaKey) || event.key !== 'Enter') return;
      event.preventDefault();
      submitRef.current?.(event.shiftKey ? { status: 'solved' } : undefined);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function discardDraft() {
    clearDraft();
    setCreatedParent(null);
    setCaller(EMPTY_CALLER);
    setFields({
      ...EMPTY_FIELDS,
      assigned_user_id: assigneeFor(user),
      form_id: config.forms.find((form) => form.is_default)?.id ?? '',
      queue_id: config.queues.find((queue) => queue.is_default)?.id ?? '',
    });
    setComposer(EMPTY_COMPOSER);
    setErrors({});
    setSubmitError(null);
    setDraftBanner(null);
    searchRef.current?.focus();
  }

  function applyMacro(macro) {
    const filled = fillMacroTemplate(macro.body_template, {
      customer: caller.customer,
      organization: caller.organization,
      location: caller.location,
      user,
      subject: composer.subject,
    });

    setComposer((prev) => ({
      ...prev,
      body_text: prev.body_text.trim() ? `${prev.body_text.trimEnd()}\n\n${filled}` : filled,
    }));

    // ELEVEN VERBS (docs/API.md §5.1), all folding into the create request rather than
    // firing a call of their own.
    //
    // EVERY VERB THE SERVER ACCEPTS MUST HAVE A BRANCH HERE. This chain silently skips
    // what it does not recognise, so a verb added to ACTION_TYPES and forgotten here
    // produces a macro that applies, does nothing, and keeps doing nothing forever with
    // no log and no symptom. `test/web/macro-apply.test.js` asserts the two lists match.
    for (const action of macro.actions ?? []) {
      // Clear `status_id` alongside it, exactly as the status picker does at the
      // `base:` branch below. A macro's `set_status` names a BASE status — the action
      // vocabulary has no `status_id` slot at all — so leaving a named status selected
      // left the two disagreeing, and `assertStatusRollsUp` refused the create with
      // `That named status rolls up to "on_hold", not "open"`. Every seeded macro 422'd
      // on top of a named status, and the agent had no way to see why: the macro looked
      // applied, because the body text and every other field had already changed.
      if (action.type === 'set_status') {
        setFields((prev) => ({ ...prev, status: action.value, status_id: null }));
      }
      else if (action.type === 'set_priority') setField('priority', action.value);
      else if (action.type === 'set_type') setField('type', action.value);
      // `current_user` is resolved HERE, at apply time, which is the whole point of it:
      // the macro means "assign this to me", and who "me" is depends on who clicked.
      else if (action.type === 'assign') {
        setField('assigned_user_id', action.value === 'current_user'
          ? assigneeFor(user) : String(action.value));
      }
      else if (action.type === 'add_tag') {
        setFields((prev) =>
          prev.tags.includes(action.value) ? prev : { ...prev, tags: [...prev.tags, action.value] },
        );
      }
      // REPLACES the list, which is what Zendesk's `set_tags` means and why it is not
      // `add_tag` in a loop. Space separated, the encoding the desk itself uses.
      else if (action.type === 'set_tags') {
        setFields((prev) => ({
          ...prev,
          tags: String(action.value).split(/\s+/).filter(Boolean),
        }));
      }
      // The mirror of `set_status`: that one clears `status_id`, so this one sets it
      // and leaves `status` for the server to roll up. Setting both from a macro is
      // how you get `assertStatusRollsUp` refusing the create.
      else if (action.type === 'set_named_status') {
        setField('status_id', String(action.value));
      }
      else if (action.type === 'set_queue') setField('queue_id', String(action.value));
      else if (action.type === 'set_form') setField('form_id', String(action.value));
      else if (action.type === 'set_subject') {
        setComposer((prev) => ({ ...prev, subject: String(action.value) }));
      }
      else if (action.type === 'add_follower') {
        const followerId = action.value === 'current_user'
          ? (user?.id ? String(user.id) : '') : String(action.value);
        if (followerId) {
          setFields((prev) => (prev.follower_user_ids.includes(followerId)
            ? prev
            : { ...prev, follower_user_ids: [...prev.follower_user_ids, followerId] }));
        }
      }
      // Keyed by `custom_field_defs.key`, which is the same shape the create request
      // sends, so nothing has to be translated on submit.
      else if (action.type === 'set_custom_field' && action.field) {
        setFields((prev) => ({
          ...prev,
          custom_fields: { ...prev.custom_fields, [action.field]: action.value },
        }));
      }
    }
  }

  const customFieldErrors = useMemo(() => {
    const mapped = {};
    for (const [key, message] of Object.entries(errors)) {
      if (key.startsWith('custom.')) mapped[key.slice(7)] = message;
    }
    return mapped;
  }, [errors]);

  const errorList = Object.values(errors);
  const statusValue = fields.status_id ? `named:${fields.status_id}` : `base:${fields.status}`;
  const isResolving = fields.status === 'solved' || fields.status === 'closed';

  const ccOptions = people.rows
    .filter((person) => person.id !== caller.customer?.id)
    .map((person) => ({
      id: person.id,
      label: person.name ?? person.email,
      hint: person.location_name ?? person.email,
    }));

  if (!canWrite) {
    return (
      <div className="rounded-lg border border-line bg-surface py-16 text-center">
        <h1 className="text-base font-semibold text-ink">New ticket</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Your role is read-only, so ticket creation is not available.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
      onKeyDown={(event) => {
        // Enter in a single-line field must not create the ticket: the account
        // number, the requester filter and the tag box all end in Enter, and an
        // implicit submit there would file a blank ticket mid-sentence.
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
          event.preventDefault();
        }
      }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-lg font-semibold text-ink">
          {fields.parent ? 'New child ticket' : fields.children.length ? `New tickets (${fields.children.length + 1})` : 'New ticket'}
        </h1>
        <p className="text-xs text-ink-muted">
          <kbd className="rounded border border-line-strong px-1">Ctrl</kbd>+
          <kbd className="rounded border border-line-strong px-1">Enter</kbd> create ·{' '}
          <kbd className="rounded border border-line-strong px-1">Ctrl</kbd>+
          <kbd className="rounded border border-line-strong px-1">Shift</kbd>+
          <kbd className="rounded border border-line-strong px-1">Enter</kbd> create &amp; solve ·{' '}
          <kbd className="rounded border border-line-strong px-1">Alt</kbd>+
          <kbd className="rounded border border-line-strong px-1">N</kbd> new contact
        </p>
      </div>

      {fields.parent && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-line-strong bg-sunken px-3 py-2 text-sm text-ink-secondary">
          <span className="min-w-0">
            Sub-issue of{' '}
            <Link to={`/tickets/${fields.parent.public_ref}`} className="font-mono text-ink hover:underline">
              #{fields.parent.public_ref}
            </Link>
            {' '}— <span className="text-ink">{fields.parent.subject}</span>. Everything editable was
            copied from it; change what differs. It is worked and closed on its own.
          </span>
          <button
            type="button"
            className="font-medium hover:underline"
            onClick={() => setField('parent', null)}
            title="Create this as a standalone ticket instead"
          >
            Detach
          </button>
        </div>
      )}

      {parentState.status === 'loading' && (
        <div className="mt-3 rounded-md border border-line-strong bg-sunken px-3 py-2 text-sm text-ink-secondary">
          Copying ticket #{parentRef}…
        </div>
      )}

      {parentState.status === 'error' && (
        <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          Could not load ticket #{parentRef} to copy from
          {parentState.error?.message ? `: ${parentState.error.message}` : '.'} This ticket will be
          created on its own.
        </div>
      )}

      {draftBanner && (
        // A transient page-level notice band, so it takes the same amber treatment
        // AppLayout's dev-stub band already uses rather than inventing a third one:
        // amber-50/900 light, filled amber-900 with amber-100 text in dark (8.2:1).
        // Deliberately NOT the note tokens — bg-note is a much darker, browner amber
        // reserved for internal notes (SPEC.md §7.4), and a banner that borrowed it
        // would teach agents that "warm card" means something it does not.
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm
                        border-amber-200 bg-amber-50 text-amber-900
                        dark:border-amber-500 dark:bg-amber-900 dark:text-amber-100">
          <span>
            Restored an unfinished ticket from {new Date(draftBanner).toLocaleString()}.
          </span>
          <span className="flex gap-2">
            <button type="button" className="font-medium hover:underline" onClick={discardDraft}>
              Discard
            </button>
            <button type="button" className="font-medium hover:underline" onClick={() => setDraftBanner(null)}>
              Keep
            </button>
          </span>
        </div>
      )}

      {config.failed.length > 0 && (
        <div className="mt-3 rounded-md border border-line-strong bg-sunken px-3 py-2 text-sm text-ink-secondary">
          Some options could not be loaded ({config.failed.join(', ')}). The ticket can still be
          created without them.
        </div>
      )}

      {/* Same explicit red pair as the queue's error card (List.jsx): there is no
          semantic error surface in the token set, and a 50/300/800 light card has to
          become a 950/900/200 dark one — a direction change, not a tint. */}
      <div
        ref={errorRef}
        tabIndex={-1}
        aria-live="polite"
        className={
          errorList.length || submitError
            ? 'mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950'
            : 'sr-only'
        }
      >
        {submitError && (
          <p className="text-sm font-medium text-red-800 dark:text-red-200">{submitError.message}</p>
        )}
        {errorList.length > 0 && (
          <ul className="list-inside list-disc text-sm text-red-800 dark:text-red-200">
            {errorList.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
      </div>

      {/* grid-rows-[auto_1fr], and it is load-bearing: the middle column spans both
          rows, and with two auto rows its height was shared between them, so a tall
          composer — several child tickets — stretched row 1 and pushed Classification
          down to the middle of the page. Items spanning an fr track are ignored when the
          auto row is sized, so row 1 is exactly the Caller panel's height and
          Classification stays directly beneath it however long the middle gets. */}
      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[22rem_minmax(0,1fr)_17rem] lg:grid-rows-[auto_1fr]">
        <div className="lg:col-start-1 lg:row-start-1">
          <CallerPanel
            value={caller}
            onChange={setCaller}
            onRequesterChosen={() => subjectRef.current?.focus()}
            searchRef={searchRef}
            people={people}
          />
        </div>

        <div className="space-y-4 lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <Composer
            value={composer}
            onChange={setComposer}
            subjectRef={subjectRef}
            canSendCustomerEmail={user.can_send_customer_email}
            macros={config.macros}
            onApplyMacro={applyMacro}
            errors={errors}
          />

          {createdParent && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500 dark:bg-amber-900 dark:text-amber-100">
              Ticket #{createdParent.public_ref} was created. The child tickets below have not been
              filed yet — fix the problem and create again to file them under it.
            </div>
          )}

          {/* Other issues from the same call, each its own ticket, filed as children of
              the one above when everything is created together (child-tickets.js). A
              child of a child is not a thing (nesting is one level), so the (+) is not
              offered while THIS ticket is itself being created as a child. */}
          {fields.children.map((child, index) => (
            <ChildTicketCard
              key={child.key}
              child={child}
              index={index}
              config={config}
              user={user}
              errors={childErrors(child)}
              onChange={updateChild}
              onRemove={() => removeChild(child.key)}
              subjectRef={(node) => {
                if (node) childSubjectRefs.current.set(child.key, node);
                else childSubjectRefs.current.delete(child.key);
              }}
            />
          ))}

          {!fields.parent && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn-ghost flex shrink-0 items-center gap-2 whitespace-nowrap ring-1 ring-line-strong"
                onClick={addChild}
                aria-label="Add another issue from this call as a child ticket"
                title="Another issue from this call — files as a child ticket of this one"
              >
                <span className="text-lg leading-none" aria-hidden="true">+</span>
                <span className="text-sm">Another issue from this call</span>
              </button>
              <span className="text-xs text-ink-muted">
                Adds a child ticket, pre-filled from this one. Each is worked and closed on its own.
              </span>
            </div>
          )}
        </div>

        <section className="grid gap-3 rounded-lg border border-line bg-surface p-4 lg:col-start-1 lg:row-start-2">
          <h2 className="text-sm font-semibold text-ink">Classification</h2>

          <div>
            <label className="field-label" htmlFor="ticket-form">Form</label>
            <select
              id="ticket-form"
              className="field-input"
              value={fields.form_id}
              onChange={(event) => setField('form_id', event.target.value)}
            >
              <option value="">-</option>
              {config.forms.map((form) => (
                <option key={form.id} value={form.id}>{form.name}</option>
              ))}
            </select>
          </div>

          {/* Which fields render is driven by the chosen form (SPEC.md §9). */}
          <CustomFields
            fields={formFields}
            values={fields.custom_fields}
            errors={customFieldErrors}
            onChange={(next) => setField('custom_fields', next)}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label" htmlFor="ticket-priority">Priority</label>
              <select
                id="ticket-priority"
                className="field-input"
                value={fields.priority}
                onChange={(event) => setField('priority', event.target.value)}
              >
                {PRIORITIES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="ticket-type">Type</label>
              <select
                id="ticket-type"
                className="field-input"
                value={fields.type ?? ''}
                onChange={(event) => setField('type', event.target.value)}
              >
                <option value="">Not categorised</option>
                {typeOptionsFor(fields.type).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {/* NO CHANNEL PICKER. This screen exists because somebody rang the desk,
                so the answer is always `phone` — it is still sent (EMPTY_FIELDS keeps
                `channel: 'phone'`), just not asked for. Email tickets do not come
                through here at all; ingestion sets `channel: 'email'` itself.

                Offering the choice invited an agent to mislabel a call as email, which
                is not cosmetic: `channel` is a routing condition and a reporting
                dimension, and SPEC.md §2.1's phone-first volume is the number
                leadership compares across the cutover. A dropdown whose only correct
                answer is the default is a way to get that number wrong. */}
          </div>

          <div>
            <label className="field-label" htmlFor="ticket-assignee">Assignee</label>
            <div className="flex gap-2">
              <select
                id="ticket-assignee"
                className="field-input"
                value={fields.assigned_user_id}
                onChange={(event) => setField('assigned_user_id', event.target.value)}
              >
                <option value="">Unassigned</option>
                {config.assignees.map((option) => (
                  <option key={option.id} value={option.id}>{option.display_name}</option>
                ))}
              </select>
              {/* "take it", carried over from the Zendesk screen (SPEC.md §6.2).
                  Only offered to users who may own a ticket — the server and the
                  database reject the rest with 403 ASSIGNEE_NOT_ASSIGNABLE. */}
              {user.can_be_assigned && (
                <button
                  type="button"
                  className="btn-ghost shrink-0 whitespace-nowrap"
                  onClick={() => setField('assigned_user_id', user.id)}
                >
                  Take it
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="ticket-queue">Queue</label>
            <select
              id="ticket-queue"
              className="field-input"
              value={fields.queue_id}
              onChange={(event) => setField('queue_id', event.target.value)}
            >
              <option value="">Default</option>
              {config.queues.map((queue) => (
                <option key={queue.id} value={queue.id}>{queue.name}</option>
              ))}
            </select>
          </div>

          <TagInput
            value={fields.tags}
            onChange={(next) => setField('tags', next)}
            suggestions={config.tags}
          />

          <div>
            <label className="field-label" htmlFor="ticket-device-serial">Device serial</label>
            <input
              id="ticket-device-serial"
              className="field-input"
              autoComplete="off"
              value={fields.device_serial}
              onChange={(event) => setField('device_serial', event.target.value)}
            />
          </div>

          <div className="flex items-end gap-2">
            <div className="min-w-0 grow">
              <ChipSelect
                id="ticket-followers"
                label="Followers"
                placeholder="Add a colleague…"
                hint="Anyone can follow. Hardware and implementation get looped in this way."
                options={config.users.map((option) => ({
                  id: option.id,
                  label: option.display_name,
                  hint: option.role,
                }))}
                value={fields.follower_user_ids}
                onChange={(next) => setField('follower_user_ids', next)}
              />
            </div>
            <button
              type="button"
              className="btn-ghost shrink-0 whitespace-nowrap"
              onClick={() =>
                setFields((prev) =>
                  prev.follower_user_ids.includes(user.id)
                    ? prev
                    : { ...prev, follower_user_ids: [...prev.follower_user_ids, user.id] },
                )
              }
            >
              Follow
            </button>
          </div>

          {/* CCs can only be set at creation in v1 (docs/API.md §7), so the one
              chance to record the colleague the caller wants copied is here. */}
          <ChipSelect
            id="ticket-ccs"
            label="CC"
            placeholder={caller.organization ? 'Copy someone else…' : 'Choose an organization first'}
            hint="External CCs receive the customer-facing replies on this ticket."
            options={ccOptions}
            value={fields.cc_customer_ids}
            onChange={(next) => setField('cc_customer_ids', next)}
          />

          {/* Related tickets (docs/API.md §3.7): "about the same thing", no behaviour.
              Collected here and linked once the ticket has a number. */}
          <div>
            <label className="field-label" htmlFor="ticket-links">Related tickets</label>
            <TicketLinkPicker
              inputId="ticket-links"
              excludeIds={fields.links.map((link) => link.id)}
              onPick={(picked) =>
                setFields((prev) =>
                  prev.links.some((link) => link.id === picked.id)
                    ? prev
                    : { ...prev, links: [...prev.links, { id: picked.id, public_ref: picked.public_ref, subject: picked.subject }] },
                )
              }
            />
            {fields.links.length > 0 && (
              <ul className="mt-2 space-y-1">
                {fields.links.map((link) => (
                  <li key={link.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate">
                      <span className="font-mono text-xs text-ink-muted">#{link.public_ref}</span>{' '}
                      <span className="text-ink">{link.subject}</span>
                    </span>
                    <button
                      type="button"
                      className="shrink-0 text-xs text-ink-secondary hover:underline"
                      onClick={() => setField('links', fields.links.filter((other) => other.id !== link.id))}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-ink-muted">
              Type a ticket number or words from its subject and pick from the matches.
            </p>
          </div>
        </section>

        <div className="lg:col-start-3 lg:row-start-1 lg:row-span-2">
          <CustomerContext
            organization={caller.organization}
            location={caller.location}
            customer={caller.customer}
          />
        </div>
      </div>

      {/* The action bar floats over scrolled content, so it needs its own elevation.
          On white a 1px rule plus a faint shadow is enough; on a dark page a
          near-black shadow does nothing against a near-black page, so the dark step
          leans on a heavier, wider shadow to keep the bar reading as "in front of"
          rather than "part of" the form. */}
      <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-t-lg border-t border-line bg-surface/95 px-3 py-3 shadow-[0_-1px_3px_rgba(15,23,42,0.08)] backdrop-blur dark:shadow-[0_-2px_8px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-2 text-xs">
          {/* SPEC.md §7.4 — this chip is the last thing an agent sees before the
              ticket is filed, and it is the only place on this screen that says
              whether the opening message reaches the customer. So it uses the note
              tokens at full strength: a filled amber-500 chip with near-black text
              (8.7:1) in BOTH themes for a note, against the cool reply pair
              (bg-reply-strong / text-reply-ink, 10:1 light and 11.1:1 dark) for a
              public reply. The two differ by hue, by fill weight and by text
              lightness, so they stay distinguishable in dark, in greyscale, and to a
              colour-blind reader — never two similar dark greys. */}
          <span
            className={`badge ${
              composer.is_internal_note
                ? 'bg-note-accent text-note-on-accent ring-note-accent font-semibold uppercase tracking-wide'
                : 'bg-reply-strong text-reply-ink ring-reply-accent'
            }`}
          >
            {composer.is_internal_note ? 'Internal note' : 'Public reply: emailed'}
          </span>
          {draftHasContent({ caller, fields, composer }, { defaultAssigneeId }) && (
            <button type="button" className="text-ink-muted hover:underline" onClick={discardDraft}>
              Discard draft
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-ink-secondary" htmlFor="submit-status">
            Submit as
          </label>
          {/* "Submit as <status>" is the create-and-solve requirement (SPEC.md §6):
              a call resolved on the call must not need creating then reopening. */}
          <select
            id="submit-status"
            className="field-input w-44 py-1.5 text-sm"
            value={statusValue}
            onChange={(event) => {
              const [kind, value] = event.target.value.split(':');
              if (kind === 'base') {
                setFields((prev) => ({ ...prev, status: value, status_id: null }));
                return;
              }
              const named = config.statuses.find((status) => status.id === value);
              if (named) {
                setFields((prev) => ({ ...prev, status: named.base_status, status_id: named.id }));
              }
            }}
          >
            {BASE_STATUSES.map(([value, label]) => (
              <option key={value} value={`base:${value}`}>{label}</option>
            ))}
            {config.statuses.length > 0 && (
              <optgroup label="Named statuses">
                {config.statuses.map((status) => (
                  <option key={status.id} value={`named:${status.id}`}>
                    {status.name} ({status.base_status.replace('_', ' ')})
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {createLabel(fields.children.length, { resolving: isResolving, submitting })}
          </button>
        </div>
      </div>
    </form>
  );
}
